import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { RefreshCw, Link2, Unlink, CheckCircle2, AlertTriangle, Clock, ArrowRight, ChevronDown, Activity, XCircle, CheckCircle, Info, X, Bell, Upload, FileText, Download, Ban, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { qboApi, projectsApi, adminApi } from '../lib/api';
import { toast } from 'sonner';

const QBOSync = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [classes, setClasses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [failedMappings, setFailedMappings] = useState([]);
  const [unmappedClasses, setUnmappedClasses] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [showClasses, setShowClasses] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [testing, setTesting] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [ignoredClasses, setIgnoredClasses] = useState([]);
  const [backfill, setBackfill] = useState(null);   // { qbo_connected, candidates, total }
  const [backfilling, setBackfilling] = useState(false);

  // Wave-4 follow-up: automated QBO ↔ LandFlow reconciliation
  const [reconReport, setReconReport] = useState(null);     // last run report
  const [reconRunning, setReconRunning] = useState(false);
  const [reconEmailing, setReconEmailing] = useState(false);
  const [reconHistory, setReconHistory] = useState([]);     // last N runs from /history
  const [expandedReconRow, setExpandedReconRow] = useState(null); // project_id of expanded variance row
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [createProjectData, setCreateProjectData] = useState({ name: '', county: '', state: '', strategy: 'Flip', qbo_class_id: '', qbo_class_name: '' });
  const [creatingProject, setCreatingProject] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statusRes, projectsRes, failedRes, unmappedRes, historyRes, notifsRes, suggestionsRes, ignoredRes, backfillRes] = await Promise.all([
        qboApi.getStatus(),
        projectsApi.getAll(),
        qboApi.getFailedMappings().catch(() => ({ data: [] })),
        qboApi.getUnmappedClasses().catch(() => ({ data: [] })),
        qboApi.getSyncHistory().catch(() => ({ data: [] })),
        qboApi.getNotifications().catch(() => ({ data: [] })),
        qboApi.getPendingSuggestions().catch(() => ({ data: [] })),
        qboApi.getIgnoredClasses().catch(() => ({ data: [] })),
        qboApi.backfillPreview().catch(() => ({ data: null })),
      ]);
      setStatus(statusRes.data);
      setProjects(projectsRes.data);
      setFailedMappings(failedRes.data);
      setUnmappedClasses(unmappedRes.data);
      setSyncHistory(historyRes.data);
      setNotifications(notifsRes.data);
      setSuggestions(suggestionsRes.data);
      setIgnoredClasses(ignoredRes.data);
      setBackfill(backfillRes.data);
    } catch (error) {
      console.error('Failed to load QBO data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    if (searchParams.get('qbo_success')) {
      toast.success('QuickBooks connected successfully!');
    } else if (searchParams.get('qbo_error')) {
      toast.error(`QuickBooks connection failed: ${searchParams.get('qbo_error')}`);
    }
  }, [loadData, searchParams]);

  const handleConnect = async () => {
    try {
      const res = await qboApi.getAuthUrl();
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to start QBO connection');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect QuickBooks? Scheduled syncs will stop.')) return;
    try {
      await qboApi.disconnect();
      toast.success('QuickBooks disconnected');
      setDiagnostics(null);
      loadData();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await qboApi.sync();
      const d = res.data;
      if (d.error) {
        toast.error(d.error);
      } else {
        toast.success(`Sync complete: ${d.synced} synced, ${d.failed} failed, ${d.skipped} skipped`);
      }
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoMap = async () => {
    setMapping(true);
    try {
      const res = await qboApi.autoMap();
      const { mapped = 0, fuzzy_mapped = 0, suggested = [], unmapped = [] } = res.data;
      const parts = [];
      if (mapped > 0) parts.push(`${mapped} exact`);
      if (fuzzy_mapped > 0) parts.push(`${fuzzy_mapped} fuzzy`);
      const matchedStr = parts.length > 0 ? `Mapped ${parts.join(' + ')} classes.` : 'No new matches.';
      const extras = [];
      if (suggested.length > 0) extras.push(`${suggested.length} suggestion${suggested.length !== 1 ? 's' : ''} to review`);
      if (unmapped.length > 0) extras.push(`${unmapped.length} unmatched`);
      toast.success(`${matchedStr} ${extras.join(', ')}`);
      loadData();
      setShowClasses(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Auto-map failed');
    } finally {
      setMapping(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion) => {
    try {
      await qboApi.acceptSuggestion({
        qbo_class_id: suggestion.qbo_class_id,
        project_id: suggestion.suggested_project_id,
      });
      toast.success(`Mapped "${suggestion.qbo_class_name}" to "${suggestion.suggested_project_name}"`);
      setSuggestions(prev => prev.filter(s => s.qbo_class_id !== suggestion.qbo_class_id));
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to accept suggestion');
    }
  };

  const handleDismissSuggestion = async (suggestion) => {
    try {
      await qboApi.dismissSuggestion({ qbo_class_id: suggestion.qbo_class_id });
      setSuggestions(prev => prev.filter(s => s.qbo_class_id !== suggestion.qbo_class_id));
      toast.info('Suggestion dismissed');
    } catch (error) {
      toast.error('Failed to dismiss suggestion');
    }
  };

  const handleIgnoreClass = async (qboClassId, qboClassName) => {
    try {
      await qboApi.ignoreClass({ qbo_class_id: qboClassId, qbo_class_name: qboClassName });
      toast.success(`"${qboClassName}" flagged as business overhead — it will be skipped on sync`);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to ignore class');
    }
  };

  const handleUnignoreClass = async (qboClassId, qboClassName) => {
    try {
      await qboApi.unignoreClass(qboClassId);
      toast.success(`"${qboClassName}" restored — it can now be mapped`);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore class');
    }
  };

  const handleBackfillClasses = async () => {
    setBackfilling(true);
    try {
      const res = await qboApi.backfillClasses();
      const { created = 0, bound = 0, failed = 0 } = res.data;
      const parts = [];
      if (created > 0) parts.push(`${created} created`);
      if (bound > 0) parts.push(`${bound} linked to existing`);
      if (failed > 0) parts.push(`${failed} failed`);
      toast.success(parts.length ? `Backfill complete: ${parts.join(', ')}` : 'Nothing to backfill');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };


  const handleCreateNewProject = (qboClassId, qboClassName) => {
    setCreateProjectData({
      name: qboClassName.replace(/^\(Cancelled\)\s*/i, ''),
      county: '',
      state: '',
      strategy: 'Flip',
      qbo_class_id: qboClassId,
      qbo_class_name: qboClassName,
    });
    setShowCreateProject(true);
  };

  const handleSubmitNewProject = async () => {
    if (!createProjectData.name) {
      toast.error('Project name is required');
      return;
    }
    setCreatingProject(true);
    try {
      const res = await projectsApi.create({
        name: createProjectData.name,
        county: createProjectData.county || undefined,
        state: createProjectData.state || undefined,
        strategy: createProjectData.strategy,
      });
      const newProjectId = res.data.id;
      
      // Auto-map the QBO class to the new project
      await qboApi.mapClass({
        qbo_class_id: createProjectData.qbo_class_id,
        qbo_class_name: createProjectData.qbo_class_name,
        project_id: newProjectId,
      });
      
      toast.success(`Created "${createProjectData.name}" and mapped to QBO class`);
      setShowCreateProject(false);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleLoadClasses = async () => {
    if (showClasses) {
      setShowClasses(false);
      return;
    }
    try {
      const res = await qboApi.getClasses();
      setClasses(res.data);
      setShowClasses(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to fetch classes');
    }
  };

  const handleMapClass = async (qboClassId, qboClassName, projectId) => {
    try {
      await qboApi.mapClass({ qbo_class_id: qboClassId, qbo_class_name: qboClassName, project_id: projectId });
      toast.success(`Mapped "${qboClassName}" to project`);
      handleLoadClasses();
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to map class');
    }
  };

  const handleUnmapClass = async (qboClassId, qboClassName) => {
    try {
      await qboApi.unmapClass(qboClassId);
      toast.success(`Unmapped "${qboClassName}"`);
      handleLoadClasses();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to unmap class');
    }
  };

  const handleClearAllMappings = async () => {
    if (!window.confirm('Clear ALL class-to-project mappings? You will need to re-map them.')) return;
    try {
      const res = await qboApi.clearAllMappings();
      toast.success(res.data.message);
      handleLoadClasses();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to clear mappings');
    }
  };

  const handleResolveFailedMapping = async (mappingId, projectId, type, category) => {
    try {
      await qboApi.resolveFailedMapping(mappingId, { project_id: projectId, type, category });
      toast.success('Mapping resolved');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to resolve mapping');
    }
  };

  const handleIgnoreFailedMapping = async (mappingId) => {
    try {
      await qboApi.ignoreFailedMapping(mappingId);
      toast.success('Mapping ignored');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to ignore mapping');
    }
  };

  // Bulk actions for failed mappings — let user select N rows and ignore /
  // assign them all to one project at once. Critical when reconciling QBO at
  // month-end where dozens of unmapped txns can pile up.
  const [selectedFailedIds, setSelectedFailedIds] = useState(new Set());
  const [bulkAssignProjectId, setBulkAssignProjectId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleFailedSelection = (id) => {
    setSelectedFailedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFailed = () => {
    if (selectedFailedIds.size === failedMappings.length) {
      setSelectedFailedIds(new Set());
    } else {
      setSelectedFailedIds(new Set(failedMappings.map(fm => fm.id)));
    }
  };

  const handleBulkIgnore = async () => {
    if (selectedFailedIds.size === 0) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of selectedFailedIds) {
      try {
        await qboApi.ignoreFailedMapping(id);
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkBusy(false);
    setSelectedFailedIds(new Set());
    if (fail > 0) {
      toast.warning(`Ignored ${ok}; ${fail} failed`);
    } else {
      toast.success(`Ignored ${ok} mappings`);
    }
    loadData();
  };

  const handleBulkAssignToProject = async () => {
    if (selectedFailedIds.size === 0 || !bulkAssignProjectId) {
      toast.error('Pick a project and at least one mapping');
      return;
    }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of selectedFailedIds) {
      const fm = failedMappings.find(m => m.id === id);
      if (!fm) continue;
      try {
        await qboApi.resolveFailedMapping(id, {
          project_id: bulkAssignProjectId,
          type: fm.suggested_type || 'Expense',
          category: fm.qbo_class_name || fm.qbo_account_name || 'Imported',
        });
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkBusy(false);
    setSelectedFailedIds(new Set());
    setBulkAssignProjectId('');
    if (fail > 0) {
      toast.warning(`Assigned ${ok}; ${fail} failed`);
    } else {
      toast.success(`Assigned ${ok} mappings to project`);
    }
    loadData();
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await qboApi.testConnection();
      setDiagnostics(res.data);
      if (res.data.overall === 'pass') {
        toast.success('All connection tests passed!');
      } else {
        toast.error('Some tests failed. Check diagnostics.');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleShowDebug = async () => {
    if (showDebug) {
      setShowDebug(false);
      return;
    }
    try {
      const res = await qboApi.debugInfo();
      setDebugInfo(res.data);
      setShowDebug(true);
    } catch (error) {
      toast.error('Failed to load debug info');
    }
  };

  const handleDismissNotification = async (notifId) => {
    try {
      await qboApi.markNotificationRead(notifId);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      toast.error('Failed to dismiss notification');
    }
  };

  const handleDismissAllNotifications = async () => {
    try {
      await qboApi.dismissAllNotifications();
      setNotifications([]);
      toast.success('All notifications dismissed');
    } catch (error) {
      toast.error('Failed to dismiss notifications');
    }
  };

  // Wave-4 follow-up: handlers for automated QBO ↔ LandFlow reconciliation.
  // The "Run Now" button computes the per-project diff inline (no email).
  // The "Email to Owner" button generates the same report and sends via Resend.
  // Monthly schedule runs server-side on the 1st at 09:00 UTC (handled by
  // /app/backend/services/qbo_scheduler.py — no UI knob needed for that).
  const handleRunReconciliation = async () => {
    setReconRunning(true);
    try {
      const r = await adminApi.runQboReconciliation();
      setReconReport(r.data);
      const s = r.data.summary || {};
      const msg = s.qbo_available
        ? `Reconciled ${s.reconciled || 0}, ${s.variance || 0} variance, ${s.no_class_mapping || 0} not mapped`
        : 'QBO not connected — showing LandFlow-only totals';
      toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconRunning(false);
    }
  };

  const handleEmailReconciliation = async () => {
    setReconEmailing(true);
    try {
      const r = await adminApi.emailQboReconciliation();
      if (r.data?.email_sent) {
        toast.success(`Reconciliation report emailed to ${r.data.recipient}`);
      } else {
        toast.error(`Email send failed: ${r.data?.email_error || 'unknown'}`);
      }
      setReconReport(r.data?.report || null);
      // Refresh history if the modal/list is showing it
      try {
        const h = await adminApi.getQboReconciliationHistory(12);
        setReconHistory(h.data?.runs || []);
      } catch (e) { console.debug('Recon history refresh failed:', e?.message); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    } finally {
      setReconEmailing(false);
    }
  };

  const handleReconcileCsv = async () => {
    if (!csvFile) return;
    setReconciling(true);
    try {
      const res = await qboApi.reconcileCsv(csvFile);
      setReconcileResult(res.data);
      toast.success(`Reconciliation complete: ${res.data.summary.matched} matched, ${res.data.summary.missing_in_landflow} missing`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  const handleImportMissing = async () => {
    if (!reconcileResult?.missing_in_landflow?.length) return;
    setImporting(true);
    try {
      const res = await qboApi.importMissing(reconcileResult.missing_in_landflow);
      toast.success(`Imported ${res.data.created} transactions, ${res.data.skipped} skipped (duplicates)`);
      setReconcileResult(null);
      setCsvFile(null);
    } catch (error) {
      toast.error('Failed to import transactions');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const isConnected = status?.connected;
  const hasTokenError = status?.token_error;

  return (
    <div className="space-y-6" data-testid="qbo-sync-page">
      {/* ============ HEADER ============ */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">QuickBooks Sync</h1>
          <p className="text-sm text-muted-foreground">Connect, map classes to projects, and sync transactions from QBO</p>
        </div>
        <div className="flex items-center gap-2">
          {isConnected && !hasTokenError && (
            <Button variant="default" size="sm" onClick={handleSync} disabled={syncing} className="rounded-sm gap-1.5" data-testid="manual-sync-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          )}
        </div>
      </div>

      {/* ============ SECTION 1: CONNECTION ============ */}
      <Card className="rounded-sm" data-testid="qbo-connection-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide">Connection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${isConnected && !hasTokenError ? 'bg-emerald-500' : isConnected && hasTokenError ? 'bg-amber-500' : 'bg-gray-300'}`} />
              <div>
                <p className="text-sm font-medium">
                  {isConnected && !hasTokenError ? 'Connected to QuickBooks' : isConnected && hasTokenError ? 'Connection Issue' : 'Not Connected'}
                  {status?.company_name && !hasTokenError && <span className="text-muted-foreground ml-1">({status.company_name})</span>}
                </p>
                {hasTokenError && <p className="text-xs text-amber-600 mt-0.5">Token error: {status.token_error}. Disconnect and reconnect to fix.</p>}
                {isConnected && !hasTokenError && status?.connected_at && <p className="text-xs text-muted-foreground">Since {new Date(status.connected_at).toLocaleDateString()}</p>}
                {!isConnected && <p className="text-xs text-muted-foreground">Click "Connect QuickBooks" to authorize access to your QBO account.</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isConnected && (
                <Button variant="outline" size="sm" onClick={handleTestConnection} disabled={testing} className="rounded-sm gap-1.5" data-testid="test-connection-btn">
                  <Activity className={`w-3.5 h-3.5 ${testing ? 'animate-pulse' : ''}`} />
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>
              )}
              {isConnected ? (
                <Button variant="outline" size="sm" onClick={handleDisconnect} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 gap-1.5" data-testid="disconnect-btn">
                  <Unlink className="w-3.5 h-3.5" /> Disconnect
                </Button>
              ) : (
                <Button onClick={handleConnect} className="rounded-sm gap-1.5" data-testid="connect-btn">
                  <Link2 className="w-3.5 h-3.5" /> Connect QuickBooks
                </Button>
              )}
            </div>
          </div>

          {/* Diagnostics */}
          {diagnostics && (
            <div className="border rounded-sm p-3 bg-muted/30 space-y-2" data-testid="diagnostics-panel">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4" />
                <p className="text-sm font-semibold">Connection Diagnostics</p>
                <Badge variant={diagnostics.overall === 'pass' ? 'default' : 'destructive'} className="text-[10px] rounded-sm ml-auto">
                  {diagnostics.overall === 'pass' ? 'ALL PASS' : 'ISSUES FOUND'}
                </Badge>
              </div>
              {diagnostics.steps?.map((step, i) => (
                <div key={`${step.name || ''}-${i}`} className="flex items-start gap-2 text-xs">
                  {step.status === 'PASS' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                   : step.status === 'WARN' ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                   : <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />}
                  <div>
                    <span className="font-medium">{step.name}:</span>{' '}
                    <span className="text-muted-foreground break-all">{step.detail}</span>
                  </div>
                </div>
              ))}
              {diagnostics.fix && (
                <div className="flex items-start gap-2 text-xs mt-2 p-2 bg-amber-50 border border-amber-200 rounded-sm">
                  <Info className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="font-medium text-amber-800">{diagnostics.fix}</span>
                </div>
              )}
            </div>
          )}

          {/* Schedule + Debug toggle */}
          <div className="flex items-center justify-between border-t pt-3">
            {isConnected && !hasTokenError && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5" />
                <span>Auto-sync: daily at 4:00 AM and 10:00 AM MST</span>
              </div>
            )}
            <button onClick={handleShowDebug} className="text-xs text-muted-foreground hover:text-foreground underline ml-auto" data-testid="show-debug-btn">
              {showDebug ? 'Hide' : 'Show'} Debug Info
            </button>
          </div>

          {showDebug && debugInfo && (
            <div className="border rounded-sm p-3 bg-slate-50 space-y-3 text-xs" data-testid="debug-info-panel">
              <div>
                <p className="font-semibold mb-1">Configuration</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-muted-foreground">
                  <p>Client ID: <span className="font-mono">{debugInfo.config?.client_id_prefix}</span></p>
                  <p>Environment: <span className="font-mono">{debugInfo.config?.environment}</span></p>
                  <p className="sm:col-span-2">Redirect URI: <span className="font-mono text-primary break-all">{debugInfo.config?.redirect_uri}</span></p>
                </div>
              </div>
              {debugInfo.connection && (
                <div>
                  <p className="font-semibold mb-1">Stored Connection</p>
                  <div className="text-muted-foreground">
                    <p>Realm: {debugInfo.connection.realm_id} | Company: {debugInfo.connection.company_name || 'Unknown'}</p>
                    <p>Expires: {debugInfo.connection.expires_at}</p>
                  </div>
                </div>
              )}
              {debugInfo.callback_logs?.length > 0 && (
                <div>
                  <p className="font-semibold mb-1">Callback Log ({debugInfo.callback_logs.length})</p>
                  <div className="space-y-1">
                    {debugInfo.callback_logs.map((log, i) => (
                      <div key={`${log.timestamp || ''}-${log.result || ''}-${i}`} className="flex items-center gap-2">
                        {log.result === 'success' ? <CheckCircle className="w-3 h-3 text-emerald-600 flex-shrink-0" /> : <XCircle className="w-3 h-3 text-red-600 flex-shrink-0" />}
                        <span className="text-muted-foreground">{log.timestamp} - {log.result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ AUTO-RECONCILIATION (always visible — works even without QBO) ============
          Lives outside the isConnected gate because the endpoint handles
          QBO-unavailable gracefully (shows LandFlow-only totals + amber
          "connect QBO to see real diffs" hint). Owner can ask the email
          report to be queued at any time.  */}
      <Card className="rounded-sm border-emerald-200" data-testid="auto-reconciliation-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
                Monthly QBO Reconciliation
              </CardTitle>
              <p className="text-[11px] text-muted-foreground mt-1">
                Per-project diff of LandFlow vs QBO. Auto-runs on the 1st of each month at 09:00 UTC and emails the owner.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm text-xs h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                onClick={handleRunReconciliation}
                disabled={reconRunning}
                data-testid="run-reconciliation-btn"
              >
                {reconRunning ? 'Running…' : 'Run now'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm text-xs h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                onClick={handleEmailReconciliation}
                disabled={reconEmailing}
                data-testid="email-reconciliation-btn"
              >
                {reconEmailing ? 'Sending…' : 'Email to owner'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {reconReport ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="p-2 rounded-sm bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700">Reconciled</div>
                  <div className="text-lg font-semibold text-emerald-700">{reconReport.summary?.reconciled || 0}</div>
                </div>
                <div className="p-2 rounded-sm bg-amber-50 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700">Variance</div>
                  <div className="text-lg font-semibold text-amber-700">{reconReport.summary?.variance || 0}</div>
                </div>
                <div className="p-2 rounded-sm bg-slate-50 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-wide text-slate-600">Not Mapped</div>
                  <div className="text-lg font-semibold text-slate-700">{reconReport.summary?.no_class_mapping || 0}</div>
                </div>
                <div className="p-2 rounded-sm bg-blue-50 border border-blue-200">
                  <div className="text-[10px] uppercase tracking-wide text-blue-700">Total Variance</div>
                  <div className="text-lg font-semibold text-blue-700 font-mono">
                    ${(reconReport.summary?.total_variance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {!reconReport.summary?.qbo_available && (
                <div className="p-2 rounded-sm bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  QBO is not connected. The report shows LandFlow-only totals; connect QBO to see real diffs.
                </div>
              )}

              {reconReport.projects?.filter(p => p.reconciliation_status === 'variance').slice(0, 8).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-[10px] uppercase text-muted-foreground">
                        <th className="text-left py-1.5">Project</th>
                        <th className="text-right py-1.5">LandFlow</th>
                        <th className="text-right py-1.5">QBO</th>
                        <th className="text-right py-1.5">Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconReport.projects.filter(p => p.reconciliation_status === 'variance').slice(0, 8).map(p => (
                        <React.Fragment key={p.project_id}>
                          <tr
                            className="border-b last:border-0 cursor-pointer hover:bg-amber-50/40"
                            onClick={() => setExpandedReconRow(expandedReconRow === p.project_id ? null : p.project_id)}
                            data-testid={`recon-row-${p.project_id}`}
                            title="Click to see specific txns missing on each side"
                          >
                            <td className="py-1.5">
                              <span className={`inline-block w-3 transition-transform ${expandedReconRow === p.project_id ? 'rotate-90' : ''}`}>›</span>
                              {p.project_name}
                            </td>
                            <td className="py-1.5 text-right font-mono">${p.landflow_net.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-1.5 text-right font-mono">${(p.qbo_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-1.5 text-right font-mono font-semibold text-amber-700">
                              ${(p.variance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                          {/* Expanded txn-level diff: which specific txns are missing on each side */}
                          {expandedReconRow === p.project_id && (
                            <tr data-testid={`recon-detail-${p.project_id}`}>
                              <td colSpan={4} className="bg-slate-50 p-3 border-b">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-1.5">
                                      In QBO but missing from LandFlow ({p.qbo_only_txns?.length || 0})
                                    </p>
                                    {p.qbo_only_txns?.length > 0 ? (
                                      <ul className="space-y-1 text-[11px]">
                                        {p.qbo_only_txns.slice(0, 10).map((t, idx) => (
                                          <li key={`qbo-${idx}`} className="border border-rose-200 bg-rose-50 px-2 py-1 rounded-sm">
                                            <span className="font-mono font-semibold">${t.amount?.toFixed(2)}</span>
                                            <span className="text-muted-foreground"> · {t.date} · </span>
                                            <span>{t.vendor || '(no vendor)'}</span>
                                            {t.memo && <div className="text-muted-foreground italic truncate">{t.memo}</div>}
                                            <div className="text-[9px] text-muted-foreground/70">QBO {t.txn_type} #{t.qbo_id}</div>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-[11px] text-muted-foreground italic">No QBO-only txns.</p>
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1.5">
                                      In LandFlow but missing from QBO ({p.landflow_only_txns?.length || 0})
                                    </p>
                                    {p.landflow_only_txns?.length > 0 ? (
                                      <ul className="space-y-1 text-[11px]">
                                        {p.landflow_only_txns.slice(0, 10).map((t, idx) => (
                                          <li key={`lf-${idx}`} className="border border-amber-200 bg-amber-50 px-2 py-1 rounded-sm">
                                            <span className="font-mono font-semibold">${t.amount?.toFixed(2)}</span>
                                            <span className="text-muted-foreground"> · {t.date} · </span>
                                            <span>{t.category || '(no category)'}</span>
                                            {t.description && <div className="text-muted-foreground italic truncate">{t.description}</div>}
                                            <div className="text-[9px] text-muted-foreground/70">{t.type} · paid by {t.responsible_party || 'unset'}</div>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-[11px] text-muted-foreground italic">No LandFlow-only txns.</p>
                                    )}
                                  </div>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-2">
                                  Matched by amount + date (±7 days). Reimbursements excluded from match pool.
                                </p>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">
                Generated {new Date(reconReport.generated_at).toLocaleString()}
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              Click <strong>Run now</strong> to compute the diff, or <strong>Email to owner</strong> to send the report immediately.
            </p>
          )}
        </CardContent>
      </Card>

      {isConnected && !hasTokenError && (
        <>
          {/* ============ SECTION 2: CLASS MAPPING ============ */}
          <Card className="rounded-sm" data-testid="class-mapping-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide">Class to Project Mapping</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={handleAutoMap} disabled={mapping} className="rounded-sm text-xs gap-1" data-testid="auto-map-btn">
                    <RefreshCw className={`w-3 h-3 ${mapping ? 'animate-spin' : ''}`} />
                    {mapping ? 'Mapping...' : 'Auto-Map'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleLoadClasses} className="rounded-sm text-xs gap-1" data-testid="view-classes-btn">
                    <ChevronDown className={`w-3 h-3 transition-transform ${showClasses ? 'rotate-180' : ''}`} />
                    {showClasses ? 'Hide Classes' : 'View All Classes'}
                  </Button>
                  {showClasses && classes.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleClearAllMappings} className="rounded-sm text-xs gap-1 text-red-600 hover:text-red-700" data-testid="clear-mappings-btn">
                      <XCircle className="w-3 h-3" /> Clear All Mappings
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Unmapped classes quick-map */}
              {unmappedClasses.length > 0 && !showClasses && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="unmapped-classes-alert">
                  <p className="text-sm font-medium text-blue-800">{unmappedClasses.length} QBO class{unmappedClasses.length !== 1 ? 'es' : ''} need mapping</p>
                  <div className="mt-2 space-y-2">
                    {unmappedClasses.map(cls => (
                      <div key={cls.qbo_class_id} className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="rounded-sm text-xs">{cls.qbo_class_name}</Badge>
                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        <Select onValueChange={(val) => {
                          if (val === 'new') {
                            handleCreateNewProject(cls.qbo_class_id, cls.qbo_class_name);
                          } else {
                            handleMapClass(cls.qbo_class_id, cls.qbo_class_name, val);
                          }
                        }}>
                          <SelectTrigger className="w-[200px] h-8 rounded-sm text-xs">
                            <SelectValue placeholder="Select project..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">+ Create New Project</SelectItem>
                            {projects.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-xs text-muted-foreground hover:text-slate-800"
                          onClick={() => handleIgnoreClass(cls.qbo_class_id, cls.qbo_class_name)}
                          data-testid={`ignore-class-${cls.qbo_class_id}`}
                          title="Skip this class on every sync (business overhead)"
                        >
                          <Ban className="w-3 h-3 mr-1" /> Ignore
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {suggestions.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="fuzzy-suggestions-alert">
                  <p className="text-sm font-medium text-amber-800">
                    {suggestions.length} suggested match{suggestions.length !== 1 ? 'es' : ''} to review
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5 mb-2">
                    These QBO classes closely match existing projects. Accept or dismiss each suggestion.
                  </p>
                  <div className="space-y-2">
                    {suggestions.map(s => (
                      <div key={s.qbo_class_id} className="flex items-center gap-2 flex-wrap bg-white p-2 rounded-sm border border-amber-100">
                        <Badge variant="outline" className="rounded-sm text-xs font-medium">{s.qbo_class_name}</Badge>
                        <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm font-medium">{s.suggested_project_name}</span>
                        <Badge className="rounded-sm text-[10px] bg-amber-100 text-amber-800 border-amber-300 ml-auto">
                          {s.confidence}% match
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          onClick={() => handleAcceptSuggestion(s)}
                          data-testid={`accept-suggestion-${s.qbo_class_id}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs text-muted-foreground hover:text-red-600"
                          onClick={() => handleDismissSuggestion(s)}
                          data-testid={`dismiss-suggestion-${s.qbo_class_id}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Dismiss
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full classes table */}
              {showClasses && classes.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">QBO Class</th>
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">LandFlow Project</th>
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">Status</th>
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classes.map(cls => (
                        <tr key={cls.qbo_class_id} className="border-b last:border-0">
                          <td className="py-2 font-medium">{cls.qbo_class_name}</td>
                          <td className="py-2">
                            {cls.is_mapped ? (
                              <Link to={`/projects/${cls.project_id}`} className="text-primary hover:underline">{cls.project_name}</Link>
                            ) : cls.is_ignored ? (
                              <span className="text-xs text-muted-foreground italic">Business overhead — not tracked</span>
                            ) : (
                              <Select onValueChange={(val) => {
                                if (val === 'new') {
                                  handleCreateNewProject(cls.qbo_class_id, cls.qbo_class_name);
                                } else {
                                  handleMapClass(cls.qbo_class_id, cls.qbo_class_name, val);
                                }
                              }}>
                                <SelectTrigger className="w-[200px] h-7 rounded-sm text-xs" data-testid={`map-select-${cls.qbo_class_id}`}>
                                  <SelectValue placeholder="Select project..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">+ Create New Project</SelectItem>
                                  {projects.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="py-2">
                            {cls.is_mapped ? (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 rounded-sm">Mapped</Badge>
                            ) : cls.is_ignored ? (
                              <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-300 rounded-sm">Ignored</Badge>
                            ) : cls.has_broken_mapping ? (
                              <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 rounded-sm">Invalid</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 rounded-sm">Unmapped</Badge>
                            )}
                          </td>
                          <td className="py-2">
                            {(cls.is_mapped || cls.has_broken_mapping) && (
                              <Button variant="ghost" size="sm" onClick={() => handleUnmapClass(cls.qbo_class_id, cls.qbo_class_name)}
                                className="h-6 px-2 text-xs text-muted-foreground hover:text-red-600" data-testid={`unmap-btn-${cls.qbo_class_id}`}>
                                <Unlink className="w-3 h-3 mr-1" /> Unmap
                              </Button>
                            )}
                            {cls.is_ignored && (
                              <Button variant="ghost" size="sm" onClick={() => handleUnignoreClass(cls.qbo_class_id, cls.qbo_class_name)}
                                className="h-6 px-2 text-xs text-muted-foreground hover:text-emerald-700" data-testid={`restore-class-${cls.qbo_class_id}`}>
                                <RotateCcw className="w-3 h-3 mr-1" /> Restore
                              </Button>
                            )}
                            {!cls.is_mapped && !cls.has_broken_mapping && !cls.is_ignored && (
                              <Button variant="ghost" size="sm" onClick={() => handleIgnoreClass(cls.qbo_class_id, cls.qbo_class_name)}
                                className="h-6 px-2 text-xs text-muted-foreground hover:text-slate-800" data-testid={`ignore-btn-${cls.qbo_class_id}`}
                                title="Skip this class on every sync (business overhead)">
                                <Ban className="w-3 h-3 mr-1" /> Ignore
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {showClasses && classes.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No QBO classes found. Create classes in QuickBooks first.</p>
              )}
            </CardContent>
          </Card>

          {/* ============ SECTION 3: FAILED MAPPINGS ============ */}
          {failedMappings.length > 0 && (
            <Card className="rounded-sm border-amber-200" data-testid="failed-mappings-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-amber-700">
                  Failed Mappings ({failedMappings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Bulk-action toolbar — appears when ≥1 row selected. */}
                <div className="mb-3 flex items-center gap-2 flex-wrap p-2 rounded-sm bg-amber-50/60 border border-amber-200" data-testid="failed-mappings-bulk-toolbar">
                  <Checkbox
                    id="select-all-failed"
                    checked={selectedFailedIds.size > 0 && selectedFailedIds.size === failedMappings.length}
                    onCheckedChange={selectAllFailed}
                    data-testid="select-all-failed-checkbox"
                  />
                  <label htmlFor="select-all-failed" className="text-xs font-medium text-amber-900 cursor-pointer select-none">
                    {selectedFailedIds.size === 0
                      ? `Select all (${failedMappings.length})`
                      : `${selectedFailedIds.size} of ${failedMappings.length} selected`}
                  </label>

                  {selectedFailedIds.size > 0 && (
                    <div className="ml-auto flex items-center gap-2 flex-wrap">
                      <select
                        value={bulkAssignProjectId}
                        onChange={(e) => setBulkAssignProjectId(e.target.value)}
                        className="text-xs h-8 px-2 rounded-sm border border-amber-300 bg-white"
                        data-testid="bulk-assign-project-select"
                      >
                        <option value="">Assign to project…</option>
                        {projects.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm text-xs h-8 border-amber-400 text-amber-800 hover:bg-amber-100"
                        onClick={handleBulkAssignToProject}
                        disabled={bulkBusy || !bulkAssignProjectId}
                        data-testid="bulk-assign-btn"
                      >
                        {bulkBusy ? 'Assigning…' : `Assign ${selectedFailedIds.size} to project`}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm text-xs h-8 border-amber-400 text-amber-800 hover:bg-amber-100"
                        onClick={handleBulkIgnore}
                        disabled={bulkBusy}
                        data-testid="bulk-ignore-btn"
                      >
                        {bulkBusy ? 'Ignoring…' : `Ignore ${selectedFailedIds.size}`}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {failedMappings.map(fm => (
                    <div key={fm.id} className="flex items-start gap-2">
                      <Checkbox
                        checked={selectedFailedIds.has(fm.id)}
                        onCheckedChange={() => toggleFailedSelection(fm.id)}
                        className="mt-3 flex-shrink-0"
                        data-testid={`select-failed-${fm.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <FailedMappingRow mapping={fm} projects={projects} onResolve={handleResolveFailedMapping} onIgnore={handleIgnoreFailedMapping} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ============ SECTION 4: SYNC HISTORY ============ */}
          <Card className="rounded-sm" data-testid="sync-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide">Sync History</CardTitle>
            </CardHeader>
            <CardContent>
              {syncHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">Time</th>
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">Trigger</th>
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">Synced</th>
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">Failed</th>
                        <th className="text-left py-2 text-xs uppercase text-muted-foreground">Skipped</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncHistory.slice(0, 20).map(log => (
                        <tr key={log.id} className="border-b last:border-0">
                          <td className="py-2 flex items-center gap-1.5">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            {new Date(log.synced_at).toLocaleString()}
                          </td>
                          <td className="py-2">
                            <Badge variant="outline" className="text-[10px] rounded-sm">{log.trigger || 'manual'}</Badge>
                          </td>
                          <td className="py-2 text-emerald-600 font-mono">{log.transactions_synced}</td>
                          <td className="py-2 text-red-600 font-mono">{log.transactions_failed}</td>
                          <td className="py-2 text-muted-foreground font-mono">{log.transactions_skipped}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No sync history yet. Click "Sync Now" to start.</p>
              )}
            </CardContent>
          </Card>

        </>
      )}

      {/* ============ Reverse Mapping / Backfill (always visible — Phase C3) ============ */}
      {backfill && backfill.total > 0 && (
        <Card className="rounded-sm border-indigo-200" data-testid="backfill-classes-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-indigo-700 flex items-center gap-2">
              <Link2 className="w-4 h-4" /> Backfill QBO Classes ({backfill.total})
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {backfill.total} LandFlow project{backfill.total !== 1 ? 's have' : ' has'} no QBO class yet.
              Create a matching class in QuickBooks for each (existing same-named classes are linked, not duplicated).
              New projects get a class automatically.
            </p>
          </CardHeader>
          <CardContent>
            {!backfill.qbo_connected && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mb-3">
                Connect QuickBooks above to enable backfill.
              </p>
            )}
            <div className="flex flex-wrap gap-1.5 mb-3 max-h-32 overflow-y-auto">
              {backfill.candidates.slice(0, 40).map(c => (
                <Badge key={c.project_id} variant="outline" className="rounded-sm text-xs" data-testid={`backfill-candidate-${c.project_id}`}>
                  {c.name}{c.is_child_lot ? ' (lot)' : ''}
                </Badge>
              ))}
              {backfill.total > 40 && (
                <Badge variant="outline" className="rounded-sm text-xs text-muted-foreground">+{backfill.total - 40} more</Badge>
              )}
            </div>
            <Button
              size="sm"
              onClick={handleBackfillClasses}
              disabled={backfilling || !backfill.qbo_connected}
              className="rounded-sm text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="backfill-run-btn"
            >
              {backfilling ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Link2 className="w-3 h-3 mr-1" />}
              {backfilling ? 'Creating classes…' : `Create & link ${backfill.total} class${backfill.total !== 1 ? 'es' : ''}`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ============ Ignored Classes (always visible — Business Overhead) ============ */}
      {ignoredClasses.length > 0 && (
        <Card className="rounded-sm border-slate-200" data-testid="ignored-classes-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-600 flex items-center gap-2">
              <Ban className="w-4 h-4" /> Ignored Classes — Business Overhead ({ignoredClasses.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              These QBO classes are skipped on every sync — their transactions never post to a project ledger. Restore one to map it to a project.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ignoredClasses.map(cls => (
                <div key={cls.qbo_class_id} className="flex items-center gap-2 flex-wrap bg-slate-50 p-2 rounded-sm border border-slate-100" data-testid={`ignored-row-${cls.qbo_class_id}`}>
                  <Ban className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <Badge variant="outline" className="rounded-sm text-xs">{cls.qbo_class_name || cls.qbo_class_id}</Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs text-emerald-700 hover:bg-emerald-50 ml-auto"
                    onClick={() => handleUnignoreClass(cls.qbo_class_id, cls.qbo_class_name)}
                    data-testid={`restore-ignored-${cls.qbo_class_id}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}


      {/* ============ CSV RECONCILIATION (always visible) ============ */}
      <Card className="rounded-sm" data-testid="csv-reconciliation-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold uppercase tracking-wide">CSV Reconciliation</CardTitle>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Upload a QBO "Transaction Detail by Account" report (CSV or XLSX) to compare and import missing transactions.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <label
                  className="flex items-center gap-2 px-3 py-2 border border-dashed rounded-sm cursor-pointer hover:bg-muted/50 transition-colors text-sm flex-1"
                  data-testid="csv-upload-label"
                >
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {csvFile ? csvFile.name : 'Choose CSV file...'}
                  </span>
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    className="hidden"
                    onChange={(e) => { setCsvFile(e.target.files[0]); setReconcileResult(null); }}
                    data-testid="csv-file-input"
                  />
                </label>
                <Button
                  size="sm"
                  className="rounded-sm"
                  disabled={!csvFile || reconciling}
                  onClick={handleReconcileCsv}
                  data-testid="reconcile-btn"
                >
                  {reconciling ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Reconcile
                </Button>
              </div>

              {reconcileResult && (
                <div className="mt-4 space-y-3" data-testid="reconcile-results">
                  {/* Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="p-2 bg-muted/30 rounded-sm text-center">
                      <p className="text-lg font-bold">{reconcileResult.summary.total_qbo_transactions}</p>
                      <p className="text-[10px] uppercase text-muted-foreground">QBO Total</p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-sm text-center">
                      <p className="text-lg font-bold text-emerald-700">{reconcileResult.summary.matched}</p>
                      <p className="text-[10px] uppercase text-muted-foreground">Matched</p>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-sm text-center">
                      <p className="text-lg font-bold text-amber-700">{reconcileResult.summary.missing_in_landflow}</p>
                      <p className="text-[10px] uppercase text-muted-foreground">Missing</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded-sm text-center">
                      <p className="text-lg font-bold text-red-700">{reconcileResult.summary.unmatched_class}</p>
                      <p className="text-[10px] uppercase text-muted-foreground">No Project</p>
                    </div>
                  </div>

                  {/* Missing transactions table */}
                  {reconcileResult.missing_in_landflow.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium">Missing in LandFlow ({reconcileResult.missing_in_landflow.length})</p>
                        <Button
                          size="sm"
                          className="rounded-sm"
                          onClick={handleImportMissing}
                          disabled={importing}
                          data-testid="import-missing-btn"
                        >
                          {importing ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                          Import All Missing
                        </Button>
                      </div>
                      <div className="overflow-x-auto border rounded-sm">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="text-left py-1.5 px-2 text-xs uppercase text-muted-foreground">Date</th>
                              <th className="text-left py-1.5 px-2 text-xs uppercase text-muted-foreground">Amount</th>
                              <th className="text-left py-1.5 px-2 text-xs uppercase text-muted-foreground">Project</th>
                              <th className="text-left py-1.5 px-2 text-xs uppercase text-muted-foreground">Type</th>
                              <th className="text-left py-1.5 px-2 text-xs uppercase text-muted-foreground">Vendor</th>
                              <th className="text-left py-1.5 px-2 text-xs uppercase text-muted-foreground">Memo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reconcileResult.missing_in_landflow.map((txn, idx) => (
                              <tr key={`${txn.date || ''}-${txn.amount || 0}-${txn.project_name || ''}-${idx}`} className="border-b last:border-0">
                                <td className="py-1.5 px-2 font-mono text-xs">{txn.date}</td>
                                <td className="py-1.5 px-2 font-mono text-xs">${txn.amount?.toFixed(2)}</td>
                                <td className="py-1.5 px-2 text-xs">{txn.project_name}</td>
                                <td className="py-1.5 px-2 text-xs text-muted-foreground">{txn.type}</td>
                                <td className="py-1.5 px-2 text-xs text-muted-foreground">{txn.name || '-'}</td>
                                <td className="py-1.5 px-2 text-xs text-muted-foreground truncate max-w-[200px]">{txn.memo || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Unmatched classes */}
                  {reconcileResult.unmatched_class.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2 text-red-700">No Project Mapping ({reconcileResult.unmatched_class.length})</p>
                      <div className="space-y-1">
                        {reconcileResult.unmatched_class.map((txn, idx) => (
                          <div key={`${txn.class_name || ''}-${txn.date || ''}-${txn.amount || 0}-${idx}`} className="p-2 bg-red-50 border border-red-100 rounded-sm text-xs flex justify-between">
                            <span>{txn.class_name} &middot; {txn.date}</span>
                            <span className="font-mono">${txn.amount?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {reconcileResult.summary.missing_in_landflow === 0 && reconcileResult.summary.unmatched_class === 0 && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm text-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                      <p className="text-sm font-medium text-emerald-700">All QBO transactions are accounted for in LandFlow!</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

      {/* ============ NOTIFICATIONS ============ */}
      {notifications.length > 0 && (
        <Card className="rounded-sm" data-testid="qbo-notifications-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold uppercase tracking-wide">Notifications ({notifications.length})</CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={handleDismissAllNotifications} className="rounded-sm text-xs text-muted-foreground hover:text-foreground" data-testid="dismiss-all-btn">
                Dismiss All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {notifications.map(n => (
                <div key={n.id} className={`p-3 rounded-sm border flex items-start gap-3 ${n.type === 'qbo_new_class' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
                  {n.type === 'qbo_new_class' ? (
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                  </div>
                  <button onClick={() => handleDismissNotification(n.id)} className="text-muted-foreground hover:text-foreground flex-shrink-0" data-testid={`dismiss-notif-${n.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create New Project Dialog */}
      <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Create a project for QBO class &ldquo;{createProjectData.qbo_class_name}&rdquo; and auto-map it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input
                value={createProjectData.name}
                onChange={(e) => setCreateProjectData(prev => ({ ...prev, name: e.target.value }))}
                className="rounded-sm"
                data-testid="create-project-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>County</Label>
                <Input
                  value={createProjectData.county}
                  onChange={(e) => setCreateProjectData(prev => ({ ...prev, county: e.target.value }))}
                  placeholder="e.g. Yadkin"
                  className="rounded-sm"
                  data-testid="create-project-county-input"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={createProjectData.state}
                  onChange={(e) => setCreateProjectData(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="e.g. NC"
                  className="rounded-sm"
                  data-testid="create-project-state-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Strategy</Label>
              <Select
                value={createProjectData.strategy}
                onValueChange={(val) => setCreateProjectData(prev => ({ ...prev, strategy: val }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="create-project-strategy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Flip">Flip</SelectItem>
                  <SelectItem value="Subdivide">Subdivide</SelectItem>
                  <SelectItem value="Hold">Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleSubmitNewProject}
              disabled={creatingProject || !createProjectData.name}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
              data-testid="submit-create-project-btn"
            >
              {creatingProject ? 'Creating & Mapping...' : 'Create Project & Map'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FailedMappingRow = ({ mapping, projects, onResolve, onIgnore }) => {
  const [projectId, setProjectId] = useState('');

  return (
    <div className="p-3 bg-amber-50/50 border border-amber-100 rounded-sm" data-testid={`failed-mapping-${mapping.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-medium">{mapping.qbo_class_name || 'Unknown Class'}</p>
          <p className="text-xs text-muted-foreground">
            {mapping.date} &middot; ${Math.abs(mapping.amount).toFixed(2)} &middot; {mapping.description || mapping.account_name || 'No description'}
          </p>
          {mapping.reason && <p className="text-xs text-amber-600 mt-1">{mapping.reason}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-[160px] h-7 rounded-sm text-xs">
              <SelectValue placeholder="Assign project..." />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs" disabled={!projectId}
            onClick={() => onResolve(mapping.id, projectId, 'Expense', mapping.account_name || 'Other')} data-testid={`resolve-mapping-${mapping.id}`}>
            <CheckCircle2 className="w-3 h-3 mr-1" /> Fix
          </Button>
          <Button variant="ghost" size="sm" className="h-7 rounded-sm text-xs text-muted-foreground hover:text-foreground"
            onClick={() => onIgnore(mapping.id)} data-testid={`ignore-mapping-${mapping.id}`}>
            Ignore
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QBOSync;
